import React, { useState, useEffect, useRef } from 'react'
import {
  Plus,
  Download,
  Activity,
  CheckCircle,
  Clock,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetDisposalService from '../../services/assetDisposalService'
import { formatDate, formatAmount, statusBadgeClass } from './assetUtils'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const AssetDisposals = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalDisposals: 0,
    draftCount: 0,
    approvedCount: 0,
    completedCount: 0,
    totalValue: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [listResponse, statsResponse] = await Promise.all([
        assetDisposalService.getAllDisposals({ limit: 1000 }),
        assetDisposalService.getDisposalStats()
      ])

      if (listResponse.success) {
        // Flatten the asset name so the table can search and sort on it
        const transformedData = (listResponse.data || []).map(item => ({
          ...item,
          id: item._id,
          assetLabel: item.acquisition?.assetName || item.acquisition?.acquisitionCode || ''
        }))
        setData(transformedData)
        if (listResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No disposal records found. Create your first disposal!')
        }
      } else {
        toast.error(listResponse.message || 'Failed to load asset disposals')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading asset disposals:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view asset disposals')
      } else if (!error._errorHandled) {
        toast.error('Failed to load asset disposal records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'disposalCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'assetLabel',
      header: 'Asset',
      cell: ({ row }) => {
        const acquisition = row.original.acquisition
        return (
          <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
            <span className="description-text">
              {acquisition ? (acquisition.assetName || acquisition.acquisitionCode || '-') : '-'}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'disposalDate',
      header: 'Disposal Date',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{formatDate(getValue())}</div>
      ),
    },
    {
      accessorKey: 'disposalReason',
      header: 'Reason',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          <span className="status-badge active">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'disposalValue',
      header: 'Disposal Value',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          {formatAmount(row.original.disposalValue, row.original.currency)}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${statusBadgeClass(getValue())}`}>{getValue() || '-'}</span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/assets/disposals/form')
  }

  const handleEdit = (record) => {
    navigate(`/assets/disposals/edit?id=${record._id}`, { state: { disposalData: record } })
  }

  const handleView = (record) => {
    navigate(`/assets/disposals/view?id=${record._id}`, { state: { disposalData: record } })
  }

  const handleDelete = (record) => {
    setDeleteItem(record)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting disposal...')
      const response = await assetDisposalService.deleteDisposal(deleteItem._id)
      if (response.success) {
        toast.success('Disposal deleted successfully!', { autoClose: 2000 })
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete disposal')
      }
    } catch (error) {
      console.error('Error deleting disposal:', error)
      if (error.response?.status === 404) {
        toast.error('Disposal not found')
      } else if (!error._errorHandled) {
        toast.error('Failed to delete disposal. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Assets', path: '/assets' },
          { label: 'Disposals', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Asset Disposals</h1>
          <p>Asset disposals and write-offs</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Disposal
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Activity size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalDisposals || 0}</div>
          <div className="metric-title">Total Disposals</div>
          <div className="metric-subtitle">All records</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Done</span>
            </div>
          </div>
          <div className="metric-value">{stats.completedCount || 0}</div>
          <div className="metric-title">Completed</div>
          <div className="metric-subtitle">Disposed assets</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{(stats.draftCount || 0) + (stats.approvedCount || 0)}</div>
          <div className="metric-title">Draft / Approved</div>
          <div className="metric-subtitle">In progress</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Value</span>
            </div>
          </div>
          <div className="metric-value">{formatAmount(stats.totalValue)}</div>
          <div className="metric-title">Disposal Value</div>
          <div className="metric-subtitle">Recovered value</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          searchable={true}
          searchPlaceholder="Search disposals..."
          noCard={true}
          pageSize={10}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Disposal"
        message={`Are you sure you want to delete disposal "${deleteItem?.disposalCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default AssetDisposals
